import {Tag, TagColor} from '@benchling/nucleus-ui';
import React, {useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import makeCustomButtonActionFunction from 'notebook/structured-tables/action-functions/makeCustomButtonActionFunction';
import unsupportedOverflowMenuItem from 'notebook/structured-tables/action-functions/unsupportedOverflowMenuItem';
import {
  SchemaFieldColumnUpdate,
  StatusFilter,
  StructuredTable,
  TableActionFunctionParameters,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cells} from 'notebook/tables/TableConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import Footer from 'ui-library/menus/Footer';
import FooterOption from 'ui-library/menus/FooterOption';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SectionHeader from 'ui-library/menus/SectionHeader';
import {capitalize} from 'util/NameUtil';

const StatusFilterToTagColor: Record<StatusFilter, TagColor> = {
  [StatusFilter.HAS_ERROR]: TagColor.ERROR,
  [StatusFilter.IS_OUT_OF_DATE]: TagColor.PURPLE,
  [StatusFilter.IS_UNSUBMITTED]: TagColor.DEFAULT,
};

function TableFilterMenu({
  selectedItems,
  setSelectedItems,
  isStale,
}: {
  selectedItems: Array<StatusFilter>;
  setSelectedItems: (v: Array<StatusFilter>) => void;
  isStale: boolean;
}): JSX.Element {
  return (
    <Menu width={Menu.WIDTHS.XXLARGE} height={Menu.HEIGHTS.MEDIUM} isMulti={true} value={[...selectedItems]}>
      <SectionHeader>
        <span className="u-base-text">Filter Row status</span>
      </SectionHeader>
      {Object.values(StatusFilter).map((filter, i) => (
        <Option
          icon={selectedItems.includes(filter) ? 'square-check-outline' : 'square-outline'}
          key={i}
          skipIconOverride={true}
          onSelect={() => {
            if (selectedItems.includes(filter)) {
              setSelectedItems(selectedItems.filter((v) => v !== filter));
            } else {
              setSelectedItems([...selectedItems, filter]);
            }
          }}
          value={filter}
        >
          <Tag text={capitalize(filter)} color={StatusFilterToTagColor[filter]} />
        </Option>
      ))}
      <Footer>
        <FooterOption
          contentClassName="u-cursor-default"
          isDisabled={true}
          skipIconOverride={true}
          // to be used by tests
          value="fake-option-button-container"
        >
          <div className="u-lr-split">
            <Button
              buttonStyle={ButtonStyle.TRANSPARENT}
              size={ButtonSize.SMALL}
              text="Clear selection"
              onClick={() => {
                setSelectedItems([]);
              }}
            />
            {isStale && (
              <Button
                icon="refresh"
                buttonStyle={ButtonStyle.TEXT}
                size={ButtonSize.SMALL}
                text="Re-apply filter"
                onClick={() => {
                  setSelectedItems(selectedItems);
                }}
              />
            )}
          </div>
        </FooterOption>
      </Footer>
    </Menu>
  );
}

function TableFilterButton<TTableMetadata, TRowMetadata>({
  isFilterStale,
  locked,
  structuredTable,
  updateHandler,
}: TableActionFunctionParameters<TTableMetadata, TRowMetadata>): JSX.Element {
  const [selectedItems, setSelectedItems] = useState(structuredTable.filterInfo?.filters ?? []);

  useEffect(() => {
    setSelectedItems(structuredTable.filterInfo?.filters ?? []);
  }, [structuredTable.filterInfo?.filters]);

  return (
    <Tooltip text={isFilterStale ? 'Filter outdated. \nRe-apply to update.' : 'Add Filter'}>
      <Dropdown
        closeOnInsideClick={false}
        menu={
          <TableFilterMenu
            selectedItems={selectedItems}
            setSelectedItems={(newVal) => {
              _updateFilterInfoHandler(newVal, {structuredTable, updateHandler});
              setSelectedItems(newVal);
            }}
            isStale={isFilterStale}
          />
        }
      >
        <Button
          icon="filter-menu"
          size={ButtonSize.MINI}
          buttonStyle={isFilterStale ? ButtonStyle.WARNING_LIGHT : ButtonStyle.TERTIARY_GRAY}
          className="u-mini-spacing-left"
          disabled={locked}
        />
      </Dropdown>
    </Tooltip>
  );
}

function _updateFilterInfoHandler<TTableMetadata, TRowMetadata>(
  filters: Array<StatusFilter>,
  {
    structuredTable,
    updateHandler,
  }: {
    updateHandler: (params: {
      updatedCells: Cells;
      columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
      filters: Array<StatusFilter>;
    }) => void;
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  }
): void {
  updateHandler({
    updatedCells: structuredTable.rows.map((row) => row.cells),
    columnUpdates: {},
    filters,
  });
}

function renderFilterButton<TTableMetadata, TTableRowMetadata>(
  actionFunctionParams: TableActionFunctionParameters<TTableMetadata, TTableRowMetadata>
): JSX.Element {
  return <TableFilterButton {...actionFunctionParams} />;
}

export const filterTableActionFunction = makeCustomButtonActionFunction({
  renderToolbarButton: renderFilterButton,
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
  getHidden: () => !BenchlingConfig.ELN_ENABLE_STRUCTURED_TABLE_STATUS_FILTERING,
});
